// @flow

import {action, makeObservable, observable, runInAction, toJS} from 'mobx';
import KlpmsService from "../services/KlpmsService";
import type {LogonResultType, SessionType} from "../model";
import t from "../trans";
import NotificationActions from "../actions/NotificationActions";
import ServiceError from "../services/ServiceError";


/**
 * Session store
 *
 * Used for keeping track logged-in status.
 *
 * Status changes:
 *   logoff - user is logged off
 *   in_progress - logon or logoff is in progress
 *   logon - user is logged on
 *   error - logon or logoff failed
 * Required roles can be:
 *      user
 *      archive
 *      admin
 *      adminPassword
 */
class SessionStore {
    // Minimum number of seconds the existing session should be valid when session store is created:
    INITIAL_MIN_SESSION_EXPIRES: number = 10;

    status: string = "logoff";
    session: SessionType = null;
    password: string = null;
    requiredRole: string = null;

    constructor() {
        this.klpmsService = new KlpmsService();

        makeObservable(this, {
            session: observable,
            status: observable,
            password: observable,
            requiredRole: observable,

            logon: action,
            logoff: action,
            setRequiredRole: action,
            invalidate: action
        });

        let localSession = this.readSessionFromLocalStorage();
        if (localSession != null && this.getExpires(localSession.expires).getTime() > Date.now() + this.INITIAL_MIN_SESSION_EXPIRES*1000 ) {
            this.session = localSession;
            this.status = "logon";
        } else {
            this.session = null;
            console.log("SessionStore.constructor: invalidating local session");
            this.storeSessionToLocalStorage(null);
            this.status = "logoff";
        }
    }

    async logon(username: string, password: string, role: ?string = null): Promise<void> {
        try {
            if (this.status === "in_progress") return;
            this.status = "in_progress";
            if (role != null) this.setRequiredRole(role);

            const response: LogonResultType = await this.klpmsService.logon(username, password);

            runInAction(() => {
                this.status = "logon";
                this.session = response.logonResult;
                this.password = password;
                if (!this.hasRequiredRole()) {
                    this.session = null;
                    this.password = null;
                    console.log("logon: invalidating local session because of role mismatch");
                    this.storeSessionToLocalStorage(null);
                    this.status = "error";
                    console.log("Invalid role: " + role);
                    NotificationActions.error.defer(t("Invalid role"));
                } else {
                    this.storeSessionToLocalStorage(response.logonResult);
                }
            });
        } catch (error) {
            runInAction(() => {
                this.session = null;
                this.password = null;
                this.storeSessionToLocalStorage(null);
                this.status = "error";
                console.log("Error executing sessionService.logon: " + error.message);
                NotificationActions.error.defer(t("Invalid user name or password"));
            });
        }
    }

    async logoff(): Promise<void> {
        try {
            if (this.status === "in_progress") return;
            this.status = "in_progress";

            await this.klpmsService.logoff();

            runInAction(() => {
                this.session = null;
                this.password = null;
                console.log("logoff: invalidating local session");
                this.storeSessionToLocalStorage(null);
                this.status = "logoff";
            });
        } catch (error) {
            runInAction(() => {
                this.session = null;
                this.password = null;
                console.log("logoff: invalidating local session because of an error");
                this.storeSessionToLocalStorage(null);
                this.status = "error";
                console.log("Error executing sessionService.logoff: " + error.message);
            });
        }
    }

    isLoggedOn(): boolean {
        return this.status == "logon";
    }

    hasRole(role: string): boolean  {
        if (this.status != "logon") return false;
        if (role == null) return true;

        switch (role) {
            case "adminPassword":
                return this.session.role == "admin" && this.getPassword() != null;
            case "admin":
                return this.session.role == "admin";
            case "archive":
                return this.session.role == "archive" || this.session.role == "admin";
            case "user":
                return true;
            default:
                // Invalid role specification:
                return false;
        }
    }

    hasRequiredRole(): boolean {
        return this.hasRole(this.requiredRole);
    }

    setRequiredRole(requiredRole: string): void {
        this.requiredRole = requiredRole;
    }

    getRequiredRoleName(): string {
        if (this.requiredRole == "adminPassword") return "admin";
        return this.requiredRole;
    }

    /**
     * Empties cached data
     */
    invalidate(): void {
        console.log("invalidate: Invalidating local session");
        this.session = null;
        this.storeSessionToLocalStorage(null);
        this.status = "logoff";
    }

    getSession(): SessionType {
        return toJS(this.session);
    }

    getToken(): string {
        return (this.session == null ? null : this.session.token);
    }

    getUsername(): string {
        return (this.session == null ? null : this.session.user);
    }

    getPassword(): string {
        return (this.password == null || this.password == "" ? null : "" + this.password);
    }

    getExpires(expires: ?string = null): Date {
        if (expires == null) {
            if (this.session == null || this.session.expires == null) return null;
            expires = this.session.expires;
        }
        return new Date(expires);
    }

    storeSessionToLocalStorage(session: ?SessionType) {
        if (session == null) localStorage.removeItem("session");
        else {
            localStorage.setItem("session", JSON.stringify(session));
        }
    }

    readSessionFromLocalStorage(): ?SessionType {
        let sessionStr = localStorage.getItem("session");
        if (sessionStr == null) return null;
        let sessionObject: SessionType = JSON.parse(sessionStr);
        // Parse expires date:
        let expires = this.getExpires(sessionObject.expires);
        if (expires <= new Date()) {
            // If session is expired OR expires soon, then remove it immediately:
            localStorage.removeItem("session");
            return null;
        }
        return sessionObject;
    }
}


export default SessionStore;
